import React, { useCallback, useEffect, useRef, useState } from 'react'
import * as S from '../styles/select-style'

interface SelectAndSearchProps<T extends { id?: number; name?: string }> {
  label: string
  options?: T[]
  fetchOptions?: (search: string) => Promise<T[]>
  minChars?: number
  onSelect: (option: T) => void
  onClear?: () => void
  selectedOption?: { id: number; name: string } | null
  disabled?: boolean
  placeholder?: string
}

function SelectAndSearch<T extends { id?: number; name?: string }>({
  label,
  options,
  fetchOptions,
  minChars = 2,
  onSelect,
  onClear,
  selectedOption,
  disabled = false,
  placeholder = 'Digite aqui',
}: SelectAndSearchProps<T>) {
  const [showOptions, setShowOptions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const [fetchedOptions, setFetchedOptions] = useState<T[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const optionsRef = useRef<HTMLUListElement>(null)
  const inputValueRef = useRef(inputValue)
  const selectedOptionRef = useRef(selectedOption)
  const justSelectedRef = useRef(false)

  const isFetchMode = !!fetchOptions

  inputValueRef.current = inputValue
  selectedOptionRef.current = selectedOption

  // Sync input with selectedOption (including clear). Depend on id/name so
  // inline selectedOption objects don't reset typing on every render.
  useEffect(() => {
    setInputValue(selectedOption?.name ?? '')
  }, [selectedOption?.id, selectedOption?.name])

  // Debounced fetch when in fetch mode
  useEffect(() => {
    if (!isFetchMode || !fetchOptions) return
    if (inputValue.length < minChars) {
      setFetchedOptions([])
      return
    }

    setIsLoading(true)
    const timeoutId = setTimeout(async () => {
      try {
        const results = await fetchOptions(inputValue)
        setFetchedOptions(results)
        setSelectedIndex(0)
      } catch (error) {
        console.error('SelectAndSearch: erro ao buscar opções', error)
        setFetchedOptions([])
      } finally {
        setIsLoading(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [inputValue, minChars, isFetchMode, fetchOptions])

  // Filtered options for local mode
  const displayOptions: T[] = isFetchMode
    ? fetchedOptions
    : (options || []).filter((opt) =>
      (opt.name || '').toLowerCase().includes(inputValue.toLowerCase()),
    )

  const handleSelectOption = useCallback(
    (option: T) => {
      if (disabled) return
      justSelectedRef.current = true
      setSelectedIndex(0)
      setInputValue(option.name || '')
      onSelect(option)
      setShowOptions(false)
    },
    [disabled, onSelect],
  )

  const handleSetOptionByKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return
      if (e.key === 'ArrowDown' && selectedIndex < displayOptions.length - 1)
        setSelectedIndex((prev) => prev + 1)
      if (e.key === 'ArrowUp' && selectedIndex > 0)
        setSelectedIndex((prev) => prev - 1)
      if (e.key === 'Enter' && displayOptions[selectedIndex])
        handleSelectOption(displayOptions[selectedIndex])
      if (e.key === 'Tab') setShowOptions(false)
    },
    [disabled, selectedIndex, displayOptions, handleSelectOption],
  )

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setShowOptions(true)
    setSelectedIndex(0)
    if (!value.trim()) {
      onClear?.()
    }
  }

  const handleBlur = () => {
    setTimeout(() => {
      setShowOptions(false)

      // Clicking an option blurs the input first; skip clear/restore in that case
      if (justSelectedRef.current) {
        justSelectedRef.current = false
        return
      }

      const current = inputValueRef.current
      const selected = selectedOptionRef.current
      if (!current.trim()) {
        // Selection already cleared via onChange; just normalize the input
        setInputValue('')
      } else if (selected?.name && current !== selected.name) {
        // Restore selected label if user typed without picking an option
        setInputValue(selected.name)
      }
    }, 250)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(target) &&
        !inputRef.current.contains(target)
      ) {
        setShowOptions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const shouldShowDropdown =
    showOptions &&
    !disabled &&
    (isFetchMode ? inputValue.length >= minChars : true)

  return (
    <S.InputDiv>
      <S.Label disabled={disabled}>{label}</S.Label>
      <S.Input
        autoComplete="off"
        type="text"
        placeholder={placeholder}
        value={inputValue}
        ref={inputRef}
        onFocus={() => !disabled && setShowOptions(true)}
        onChange={handleChangeInput}
        onBlur={handleBlur}
        onKeyDown={handleSetOptionByKey}
        disabled={disabled}
      />
      {shouldShowDropdown && (
        <S.SearchOptions ref={optionsRef}>
          {isLoading && isFetchMode ? (
            <S.Option isSelected={false}>Buscando...</S.Option>
          ) : displayOptions.length > 0 ? (
            displayOptions.map((option, index) => (
              <S.Option
                key={option.id}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelectOption(option)
                }}
                isSelected={index === selectedIndex}
              >
                {option.name}
              </S.Option>
            ))
          ) : (
            <S.Option isSelected={false}>
              {isFetchMode
                ? 'Nenhum resultado encontrado'
                : 'Nenhuma opção disponível'}
            </S.Option>
          )}
        </S.SearchOptions>
      )}
    </S.InputDiv>
  )
}

export default SelectAndSearch
